import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Star, Users, ShieldCheck } from "lucide-react";
import { IMAGES } from "@/lib/site";

const lines = ["One tutor.", "One child.", "Infinite curiosity."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

export const Hero = ({ onDemo, onPlans }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" className="relative min-h-[92vh] pt-32 md:pt-36 overflow-hidden">
      <div className="noise absolute inset-0 opacity-[0.04] mix-blend-multiply pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div style={{ y }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-accent/40 border border-accent/60 px-4 py-2 mb-7"
          >
            <span className="h-2 w-2 rounded-full bg-secondary animate-pulse" />
            <span className="font-label text-xs tracking-wide font-medium text-foreground/80">Live 1-on-1 & small-batch classes · Grades 1–8</span>
          </motion.div>

          <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] text-foreground">
            {lines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
                  {i === 2 ? (
                    <>Infinite <span className="text-secondary italic">curiosity.</span></>
                  ) : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed"
          >
            Personalised online tutoring built around your child's grade, board and syllabus, with structured weekly classes, regular assessments and doubt-clearing that actually move the needle.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <button data-testid="hero-demo-btn" onClick={onDemo} className="rounded-full bg-secondary text-secondary-foreground px-8 py-4 font-bold hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300">
              Book a Free Demo Class
            </button>
            <button data-testid="hero-plans-btn" onClick={onPlans} className="rounded-full border-2 border-primary text-primary px-8 py-4 font-bold hover:bg-primary hover:text-white transition-colors duration-300">
              Explore Plans
            </button>
          </motion.div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
            <div className="flex items-center gap-2"><ShieldCheck className="text-primary" size={20} /><span className="font-label text-sm text-foreground/70">Syllabus-aligned</span></div>
            <div className="flex items-center gap-2"><Users className="text-primary" size={20} /><span className="font-label text-sm text-foreground/70">1-on-1 & small batches</span></div>
            <div className="flex items-center gap-2"><Star className="text-accent fill-accent" size={20} /><span className="font-label text-sm text-foreground/70">4.9/5 parent rating</span></div>
          </div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-secondary/10 rounded-[2.5rem] rotate-3" />
            <div className="absolute -inset-4 bg-primary/10 rounded-[2.5rem] -rotate-3" />
            <img
              src={IMAGES.hero}
              alt="Child learning online with headphones"
              className="relative rounded-[2rem] w-full object-cover aspect-[4/5] shadow-2xl"
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
              className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 border border-black/5"
            >
              <div className="font-heading font-black text-3xl text-primary">12k+</div>
              <div className="font-label text-xs text-muted-foreground">classes delivered</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
